import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { getTodasModificaciones, getUsers } from "../../api/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function HistorialModificaciones() {
  const { token, user } = useContext(AuthContext);
  const [modificaciones, setModificaciones] = useState([]);
  const [modificacionesFiltradas, setModificacionesFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  
  // Filtros
  const [fechaInicio, setFechaInicio] = useState(() => {
    const hace30Dias = new Date();
    hace30Dias.setDate(hace30Dias.getDate() - 30);
    return hace30Dias;
  });
  const [fechaFin, setFechaFin] = useState(new Date());
  const [empleadoFiltro, setEmpleadoFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [busqueda, setBusqueda] = useState("");

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 20;

  useEffect(() => {
    cargarEmpleados();
  }, []);

  useEffect(() => {
    cargarModificaciones();
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    filtrarModificaciones();
  }, [modificaciones, empleadoFiltro, tipoFiltro, busqueda]);

  const cargarEmpleados = async () => {
    try {
      const data = await getUsers(token);
      setEmpleados(data);
    } catch (error) {
    }
  };

  const cargarModificaciones = async () => {
    setLoading(true);
    try {
      const desde = fechaInicio.toISOString().split('T')[0];
      const hasta = fechaFin.toISOString().split('T')[0];
      
      const data = await getTodasModificaciones(token, { desde, hasta });
      setModificaciones(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filtrarModificaciones = () => {
    let filtradas = [...modificaciones];

    // Filtrar por empleado
    if (empleadoFiltro) {
      filtradas = filtradas.filter(m => 
        m.empleado && String(m.empleado._id) === String(empleadoFiltro)
      );
    }

    // Filtrar por tipo de modificación
    if (tipoFiltro) {
      filtradas = filtradas.filter(m => m.tipoModificacion === tipoFiltro);
    }

    // Filtrar por búsqueda
    if (busqueda) {
      const busquedaLower = busqueda.toLowerCase();
      filtradas = filtradas.filter(m => {
        const nombreEmpleado = m.empleado 
          ? `${m.empleado.nombre} ${m.empleado.primerApellido || ''} ${m.empleado.segundoApellido || ''}`.toLowerCase()
          : '';
        const nombreAdmin = m.modificadoPor
          ? `${m.modificadoPor.nombre} ${m.modificadoPor.primerApellido || ''} ${m.modificadoPor.segundoApellido || ''}`.toLowerCase()
          : '';
        const motivo = (m.motivo || '').toLowerCase();
        
        return nombreEmpleado.includes(busquedaLower) || 
               nombreAdmin.includes(busquedaLower) || 
               motivo.includes(busquedaLower);
      });
    }

    setModificacionesFiltradas(filtradas);
    setPaginaActual(1);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const obtenerIconoTipo = (tipo) => {
    switch (tipo) {
      case 'crear': return { icon: 'fa-plus-circle', color: 'text-green-600', bg: 'bg-green-100' };
      case 'editar': return { icon: 'fa-edit', color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'eliminar': return { icon: 'fa-trash', color: 'text-red-600', bg: 'bg-red-100' };
      default: return { icon: 'fa-question-circle', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const obtenerTextoTipo = (tipo) => {
    switch (tipo) {
      case 'crear': return 'Crear';
      case 'editar': return 'Editar';
      case 'eliminar': return 'Eliminar';
      default: return tipo;
    }
  };

  const obtenerNombreCampo = (campo) => {
    const nombres = {
      tipo: 'Tipo',
      fecha: 'Fecha y hora'
    };
    return nombres[campo] || campo;
  };

  // Paginación
  const indiceInicio = (paginaActual - 1) * itemsPorPagina;
  const indiceFin = indiceInicio + itemsPorPagina;
  const modificacionesPaginadas = modificacionesFiltradas.slice(indiceInicio, indiceFin);
  const totalPaginas = Math.ceil(modificacionesFiltradas.length / itemsPorPagina);

  return (
    <div className="p-4 md:p-0">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Historial de Modificaciones
        </h1>
        <p className="text-gray-600">
          Registro de todas las modificaciones realizadas en los fichajes
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="fas fa-filter text-gray-600"></i>
          Filtros
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Fecha Inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Inicio
            </label>
            <DatePicker
              selected={fechaInicio}
              onChange={setFechaInicio}
              dateFormat="dd/MM/yyyy"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxDate={fechaFin}
            />
          </div>

          {/* Fecha Fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Fin
            </label>
            <DatePicker
              selected={fechaFin}
              onChange={setFechaFin}
              dateFormat="dd/MM/yyyy"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              minDate={fechaInicio}
              maxDate={new Date()}
            />
          </div>

          {/* Empleado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Empleado
            </label>
            <select
              value={empleadoFiltro}
              onChange={(e) => setEmpleadoFiltro(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos los empleados</option>
              {empleados.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.nombre} {emp.primerApellido || ''} {emp.segundoApellido || ''}
                </option>
              ))}
            </select>
          </div>

          {/* Tipo de modificación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Modificación
            </label>
            <select
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos los tipos</option>
              <option value="crear">Crear</option>
              <option value="editar">Editar</option>
              <option value="eliminar">Eliminar</option>
            </select>
          </div>
        </div>

        {/* Búsqueda */}
        <div className="mt-4">
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Buscar por empleado, administrador o motivo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Resultados */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            <strong>{modificacionesFiltradas.length}</strong> modificaciones encontradas
          </span>
          <button
            onClick={() => {
              setEmpleadoFiltro("");
              setTipoFiltro("");
              setBusqueda("");
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            <i className="fas fa-times mr-1"></i>
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
            <p className="text-gray-600">Cargando modificaciones...</p>
          </div>
        </div>
      )}

      {/* Tabla de modificaciones */}
      {!loading && modificacionesPaginadas.length > 0 && (
        <>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Fecha Modificación
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Empleado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Campo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Valor Anterior
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Valor Nuevo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Modificado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Motivo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {modificacionesPaginadas.map((mod) => {
                    const tipoInfo = obtenerIconoTipo(mod.tipoModificacion);
                    return (
                      <tr key={mod._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatearFecha(mod.fechaModificacion)}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tipoInfo.bg} ${tipoInfo.color}`}>
                            <i className={`fas ${tipoInfo.icon}`}></i>
                            {obtenerTextoTipo(mod.tipoModificacion)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {mod.empleado 
                            ? `${mod.empleado.nombre} ${mod.empleado.primerApellido || ''} ${mod.empleado.segundoApellido || ''}`.trim()
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {obtenerNombreCampo(mod.campo)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {mod.valorAnterior 
                            ? (mod.campo === 'fecha' ? formatearFecha(mod.valorAnterior) : mod.valorAnterior)
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          {mod.valorNuevo 
                            ? (mod.campo === 'fecha' ? formatearFecha(mod.valorNuevo) : mod.valorNuevo)
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {mod.modificadoPor
                            ? `${mod.modificadoPor.nombre} ${mod.modificadoPor.primerApellido || ''} ${mod.modificadoPor.segundoApellido || ''}`.trim()
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {mod.motivo || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Mostrando {indiceInicio + 1} a {Math.min(indiceFin, modificacionesFiltradas.length)} de {modificacionesFiltradas.length}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                  disabled={paginaActual === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-left mr-2"></i>
                  Anterior
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                    let pageNum;
                    if (totalPaginas <= 5) {
                      pageNum = i + 1;
                    } else if (paginaActual <= 3) {
                      pageNum = i + 1;
                    } else if (paginaActual >= totalPaginas - 2) {
                      pageNum = totalPaginas - 4 + i;
                    } else {
                      pageNum = paginaActual - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPaginaActual(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium ${
                          paginaActual === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                  disabled={paginaActual === totalPaginas}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                  <i className="fas fa-chevron-right ml-2"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Sin resultados */}
      {!loading && modificacionesFiltradas.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <i className="fas fa-inbox text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            No se encontraron modificaciones
          </h3>
          <p className="text-gray-600">
            {modificaciones.length === 0 
              ? "No hay modificaciones en el rango de fechas seleccionado."
              : "Intenta ajustar los filtros para ver más resultados."
            }
          </p>
        </div>
      )}
    </div>
  );
}
